import { FMoney } from 'js/utils/GLOBAL'

// Função inteligente para interpretar diferentes formatos de entrada
const parseFlexibleCurrency = (value) => {
  if (!value || value === '') return 0

  const cleanValue = String(value).trim()

  // Remove espaços e caracteres especiais, mantendo apenas números, pontos e vírgulas
  const numbersOnly = cleanValue.replace(/[^\d.,]/g, '')

  // Se não tem separadores, é um número inteiro
  if (!/[.,]/.test(numbersOnly)) {
    return parseFloat(numbersOnly) || 0
  }

  // Encontra a posição do último ponto e da última vírgula
  const lastDotIndex = numbersOnly.lastIndexOf('.')
  const lastCommaIndex = numbersOnly.lastIndexOf(',')

  // Determina qual é o separador decimal baseado na posição e contexto
  let decimalSeparatorIndex = -1
  let decimalSeparator = ''

  // Se tem ambos separadores
  if (lastDotIndex > -1 && lastCommaIndex > -1) {
    // O último separador é o decimal se seguido por 1-2 dígitos
    if (lastCommaIndex > lastDotIndex) {
      const afterComma = numbersOnly.substring(lastCommaIndex + 1)
      if (afterComma.length <= 2) {
        decimalSeparatorIndex = lastCommaIndex
        decimalSeparator = ','
      }
    } else {
      const afterDot = numbersOnly.substring(lastDotIndex + 1)
      if (afterDot.length <= 2) {
        decimalSeparatorIndex = lastDotIndex
        decimalSeparator = '.'
      }
    }
  }
  // Se tem apenas vírgula
  else if (lastCommaIndex > -1) {
    const afterComma = numbersOnly.substring(lastCommaIndex + 1)
    // Se há 3 ou mais dígitos após a vírgula, trata como separador de milhares
    if (afterComma.length === 3) {
      // "1,520" → separador de milhares
      decimalSeparatorIndex = -1
    } else if (afterComma.length <= 2) {
      // "1,5" ou "1,50" → decimal
      decimalSeparatorIndex = lastCommaIndex
      decimalSeparator = ','
    }
  }
  // Se tem apenas ponto
  else if (lastDotIndex > -1) {
    const afterDot = numbersOnly.substring(lastDotIndex + 1)
    const beforeDot = numbersOnly.substring(0, lastDotIndex)

    // Se há 3 dígitos após o ponto e o número antes tem mais de 1 dígito, trata como separador de milhares
    if (afterDot.length === 3 && beforeDot.length > 1) {
      // "1.520" → separador de milhares
      decimalSeparatorIndex = -1
    } else if (afterDot.length <= 2) {
      // "1.5" ou "1.50" → decimal
      decimalSeparatorIndex = lastDotIndex
      decimalSeparator = '.'
    }
  }

  let result = 0

  if (decimalSeparatorIndex > -1) {
    // Tem parte decimal
    const integerPart = numbersOnly
      .substring(0, decimalSeparatorIndex)
      .replace(/[.,]/g, '')
    const decimalPart = numbersOnly.substring(
      decimalSeparatorIndex + 1
    )
    result = parseFloat(integerPart + '.' + decimalPart)
  } else {
    // Não tem parte decimal, remove todos os separadores
    result = parseFloat(numbersOnly.replace(/[.,]/g, ''))
  }

  return result || 0
}

// v-money.js - Diretiva otimizada para formatação de valores monetários
export default {
  mounted(el, binding) {
    // Verifica se o elemento é um input
    if (el.tagName !== 'INPUT') {
      console.warn('v-money deve ser usado apenas em elementos input')
      return
    }

    // Função otimizada para manter posição do cursor
    const maintainCursorPosition = (
      element,
      oldValue,
      newValue,
      cursorPos
    ) => {
      const oldLength = oldValue.length
      const newLength = newValue.length
      const diff = newLength - oldLength

      // Calcula nova posição do cursor de forma mais precisa
      let newCursorPos = cursorPos + diff

      // Garante que o cursor não saia dos limites
      newCursorPos = Math.max(0, Math.min(newCursorPos, newLength))

      // Define posição do cursor
      setTimeout(() => {
        element.setSelectionRange(newCursorPos, newCursorPos)
      }, 0)
    }

    // Handler otimizado para o evento input
    const handleInput = (e) => {
      const element = e.target
      const oldValue = element.value
      const cursorPos = element.selectionStart

      // Ignora se o valor veio de um paste (evita reprocessamento)
      if (element._fromPaste) {
        delete element._fromPaste
        return
      }

      // Para entrada normal, usa formatação simples
      // Para valores colados ou complexos, usa a função inteligente
      let formattedValue
      if (oldValue.includes(',') && oldValue.includes('.')) {
        const parsedValue = parseFlexibleCurrency(oldValue)
        formattedValue = FMoney(parsedValue.toString())
      } else {
        formattedValue = FMoney(oldValue)
      }

      // Só atualiza se o valor mudou (evita loops desnecessários)
      if (formattedValue !== oldValue) {
        element.value = formattedValue
        maintainCursorPosition(
          element,
          oldValue,
          formattedValue,
          cursorPos
        )
      }
    }

    // Handler para evento paste (colar)
    const handlePaste = (e) => {
      e.preventDefault()
      const pastedText = e.clipboardData.getData('text')

      // Usa a função inteligente para interpretar o valor colado
      const parsedValue = parseFlexibleCurrency(pastedText)
      
      // Como parseFlexibleCurrency retorna o valor já em reais,
      // formatamos diretamente sem usar FMoney (que divide por 100)
      const formattedValue = parsedValue.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).replace('R$', '').trim()

      e.target.value = formattedValue
      
      // Marca que o valor veio de paste para evitar reprocessamento no handleInput
      e.target._fromPaste = true
      
      console.log('handlePaste Pasted text:', {
        pastedText,
        parsedValue,
        formattedValue,
      })

      // Dispara evento de input para sincronizar com Vue
      const inputEvent = document.createEvent('Event')
      inputEvent.initEvent('input', true, true)
      e.target.dispatchEvent(inputEvent)
    }

    // Handler para evento blur (sair do campo)
    const handleBlur = (e) => {
      // Garante formatação correta ao sair do campo
      const formattedValue = FMoney(e.target.value || '0')
      e.target.value = formattedValue
    }

    // Adiciona listeners
    el.addEventListener('input', handleInput)
    el.addEventListener('paste', handlePaste)
    el.addEventListener('blur', handleBlur)

    // Formata valor inicial
    const initialValue = el.value || binding.value || '0'
    el.value = FMoney(initialValue.toString())

    // Armazena referências para cleanup
    el._moneyHandlers = {
      input: handleInput,
      paste: handlePaste,
      blur: handleBlur,
    }
  },

  unmounted(el) {
    // Cleanup dos event listeners para evitar memory leaks
    if (el._moneyHandlers) {
      el.removeEventListener('input', el._moneyHandlers.input)
      el.removeEventListener('paste', el._moneyHandlers.paste)
      el.removeEventListener('blur', el._moneyHandlers.blur)
      delete el._moneyHandlers
    }
  },

  updated(el, binding) {
    // Atualiza valor se o binding mudar
    if (binding.value !== binding.oldValue) {
      el.value = FMoney((binding.value || '0').toString())
    }
  },
}
